import React, { useEffect, useState } from 'react';
import useDragTranslate from '../hooks/useDragTranslate';
import { getRandomWindowPosition } from '../utils/windowPositioning';
/* project detail window component */

const ProjectDetailWindow = ({ onClose, zIndex, onFocus, projectData }) => {
  const { elRef, onPointerDown, onPointerMove, onPointerUp } = useDragTranslate(onFocus);
  /* drag functionality for this window */
  
  const [currentSlide, setCurrentSlide] = useState(0);
  /* slideshow state */
  
  // Generate random position for this window
  const [windowPosition] = useState(() => getRandomWindowPosition(900, 900));
  
  const handleFocus = () => {
    onFocus && onFocus();
    /* bring window to front when clicked */
  };

  const handleClose = () => {
    // 1) add .closing
    elRef.current?.classList.add('closing');
    /* start the close animation */

    // 2) after animation end, call onClose to unmount
    const done = () => {
      elRef.current?.removeEventListener('animationend', done);
      onClose();
    };
    elRef.current?.addEventListener('animationend', done, { once: true });
    /* wait for animation to finish then actually close */
  };

  const nextSlide = () => {
    const gallery = projectData?.sections?.find(section => section.title === 'Gallery');
    if (gallery && gallery.content) {
      setCurrentSlide((prev) => (prev + 1) % gallery.content.length);
    }
  };

  const prevSlide = () => {
    const gallery = projectData?.sections?.find(section => section.title === 'Gallery');
    if (gallery && gallery.content) {
      setCurrentSlide((prev) => (prev - 1 + gallery.content.length) % gallery.content.length);
    }
  };
  /* slideshow navigation functions */

  useEffect(() => {
    // listen on the document so we can move outside the window bounds
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    /* set up drag event listeners */
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      /* clean up listeners */
    };
  }, [onPointerMove, onPointerUp]);

  return (
    <div
      ref={elRef}
      className="window"
      data-window="project-detail"
      style={{
        display: 'block',
        width: '900px',
        height: '900px',
        zIndex: zIndex,
        position: 'fixed',
        '--tx': `${windowPosition.tx}px`,
        '--ty': `${windowPosition.ty}px`,
        // important: do not set left/top/transform here; css handles it.
      }}
      onClick={handleFocus}
    >
      <div className="title-bar draggable" onPointerDown={onPointerDown}>
        <div className="title-bar-text">{projectData?.title || 'Project Details'}</div>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={handleClose}></button>
        </div>
      </div>
      {/* window title bar with close button */}
      
      <div className="window-body project-detail-body">
        <div className="project-header">
          <div className="project-header-content">
            <div className="project-title-section">
              <h1 className="project-title">{projectData?.title}</h1>
              <p className="project-subtitle">{projectData?.subtitle}</p>
            </div>
            <div className="project-header-links">
            </div>
          </div>
        </div>
        {/* project header with title and subtitle */}

        {projectData?.image && (
          <div className="project-image-container">
            <img src={projectData.image} alt={projectData.title} className="project-image" />
          </div>
        )}
        {/* project image if available */}

        <div className="project-content">
          {projectData?.sections?.map((section, index) => (
            <div key={index} className="project-section">
              <h2 className="section-title">{section.title}</h2>
              <div className="section-content">
                {section.title === 'Gallery' && section.content ? (
                  <div className="slideshow-container">
                    <div className="slideshow-image-container">
                      {section.content[currentSlide]?.type === 'video' ? (
                        <video 
                          src={section.content[currentSlide]?.src} 
                          controls
                          className="slideshow-image"
                          preload="metadata"
                          style={{ display: 'block' }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img 
                          src={section.content[currentSlide]?.src} 
                          alt={section.content[currentSlide]?.caption || 'Project image'} 
                          className="slideshow-image clickable-image"
                          onClick={() => window.open(section.content[currentSlide]?.src, '_blank')}
                        />
                      )}
                    </div>
                    <div className="slideshow-caption">
                      <div dangerouslySetInnerHTML={{ __html: section.content[currentSlide]?.caption || 'No caption available' }}></div>
                    </div>
                    {section.content.length > 1 && (
                      <div className="slideshow-controls">
                        <button className="slideshow-arrow slideshow-prev" onClick={prevSlide}>
                          ‹
                        </button>
                        <div className="slideshow-indicator">
                          {currentSlide + 1} / {section.content.length}
                        </div>
                        <button className="slideshow-arrow slideshow-next" onClick={nextSlide}>
                          ›
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  section.content.map((item, pIndex) => (
                    item.type === 'video' ? (
                      <div key={pIndex} className="project-video-container">
                        <video 
                          src={item.src} 
                          controls
                          className="project-video"
                          preload="metadata"
                          style={{ display: 'block' }}
                        >
                          Your browser does not support the video tag.
                        </video>
                        {item.caption && (
                          <div className="project-video-caption" dangerouslySetInnerHTML={{ __html: item.caption }}>
                          </div>
                        )}
                      </div>
                    ) : item.type === 'download' ? (
                      <div key={pIndex} className="project-download-container">
                        <a
                          href={item.file}
                          download
                          className="project-download-button"
                          data-analytics-id="download_one_way_out"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        >
                        </a>
                        {item.caption && (
                          <div className="project-download-caption" dangerouslySetInnerHTML={{ __html: item.caption }}>
                          </div>
                        )}
                      </div>
                    ) : item.type === 'image' ? (
                      <div key={pIndex} className="project-image-container">
                        <img 
                          src={item.src} 
                          alt={item.caption || 'Project image'} 
                          className="project-gallery-image clickable-image"
                          onClick={() => window.open(item.src, '_blank')}
                        />
                        {item.caption && (
                          <div className="project-image-caption" dangerouslySetInnerHTML={{ __html: item.caption }}>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p key={pIndex} className={`project-paragraph ${item.type || ''}`} dangerouslySetInnerHTML={{ __html: item.text }}>
                      </p>
                    )
                  ))
                )}
              </div>
            </div>
          ))}
          {/* project sections with different content types */}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailWindow;
